import type { FC } from "react";
import { TriggerButton } from "widgets";
import type { Either } from "widgets/utils";

import { useError } from "@/app-utils/graphql/useError.ts";
import { type FileDownloadQuery, useFileDownloadQuery } from "@/modules/files/graphql/files-queries.gql";
import { notify } from "@/modules/notifications/notify.tsx";
import { FILE_BROWSE_TYPES } from "@/settings/settings.ts";
  import { checkProjectFileExists, downloadProjectFile } from "@selector/shared/jsx";
import { selMessage } from "@selector/shared";

interface DownloadByIdProps {
  fileId: number;
}

interface DownloadByFileProps {
  file: FileDownloadQuery["projectFileById"] | undefined;
}

/**
 * {@link TriggerButton} to download (single) project files. Download of multiple
 * files is yet not supported.
 * @param props fileId
 * @constructor
 */
export const FileDownloadButton: FC<Either<DownloadByIdProps, DownloadByFileProps>> = ({ fileId, file }) => {
  const { data, error } = useFileDownloadQuery({ variables: { fileId: fileId ?? -1 }, skip: !fileId });
  void useError(error);

  const downloadableFile: FileDownloadQuery["projectFileById"] | null | undefined = file ?? data?.projectFileById;

  return downloadableFile?.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE ? (
    <TriggerButton
      onPress={() =>
        void checkProjectFileExists(downloadableFile.id).then((exists) => {
          if (exists) {
            downloadProjectFile(downloadableFile);
          } else {
            notify.warning(
              {
                title: selMessage({
                  id: "file.download.failed.title",
                }),
                children: selMessage({
                  id: "file.download.failed.children",
                  values: { fileNames: downloadableFile?.fileName, filesCount: 1 },
                }),
              },
              { autoClose: 5000 },
            );
          }
        })
      }
      icon={"download"}
      key={`file-download-button-${downloadableFile.id}`}
      label={"Download"}
      showLabel
    />
  ) : null;
};
